import React, { useEffect, useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { MessageCircle, Share2, Download, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function MyQR() {
  const [qr, setQr] = useState(null);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  useEffect(() => {
    api.get('/architect/qr').then((r) => setQr(r.data)).catch(() => {});
  }, []);

  const shareText = () =>
    `Bella Maison alışverişinizde kasada bu mimar kodumu gösterin; size özel indirim otomatik uygulanır.\nMimar kodum: ${qr.qrCode} (${qr.name})`;

  const pngDataUrl = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    return canvas ? canvas.toDataURL('image/png') : null;
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText())}`, '_blank');
  };

  const share = async () => {
    if (!qr) return;
    const dataUrl = pngDataUrl();
    try {
      if (dataUrl && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], `mimar-kodu-${qr.qrCode}.png`, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'Bella Maison Mimar Kodu', text: shareText() });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: 'Bella Maison Mimar Kodu', text: shareText() });
        return;
      }
    } catch { return; }
    navigator.clipboard?.writeText(shareText());
    toast.success('Kod metni kopyalandı');
  };

  const download = () => {
    const dataUrl = pngDataUrl();
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `mimar-kodu-${qr.qrCode}.png`;
    a.click();
    toast.success('QR kod indirildi');
  };

  const copyCode = () => {
    if (!qr) return;
    navigator.clipboard?.writeText(qr.qrCode);
    setCopied(true);
    toast.success('Mimar kodu kopyalandı');
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="px-5 py-8 flex flex-col items-center fade-up" data-testid="my-qr-page">
      <p className="mini-label mb-1">QR Kodum</p>
      <h1 className="font-serif text-2xl text-ink mb-8">Kişisel Kodunuz</h1>

      {/* White card — QR must stay dark-on-light for scannability */}
      <div className="bg-white border border-line rounded-2xl p-8" data-testid="qr-container">
        {qr ? (
          <QRCodeSVG value={qr.qrCode} size={232} bgColor="#FFFFFF" fgColor="#33414E" level="M" />
        ) : (
          <div className="w-[232px] h-[232px] flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-ink/20 border-t-ink rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Hidden canvas used to generate a downloadable / shareable PNG */}
      {qr && (
        <div ref={canvasRef} className="hidden" aria-hidden="true">
          <QRCodeCanvas value={qr.qrCode} size={512} bgColor="#FFFFFF" fgColor="#33414E" level="M" includeMargin />
        </div>
      )}

      {qr && (
        <div className="mt-6 text-center">
          <p className="font-serif text-xl text-ink" data-testid="qr-name">{qr.name}</p>
          <button
            onClick={copyCode}
            data-testid="qr-copy-code-btn"
            className="text-xs text-ink-soft tracking-wide mt-1 inline-flex items-center gap-1.5 hover:text-ink transition-colors"
          >
            Mimar · {qr.qrCode}
            {copied ? <Check strokeWidth={1.5} className="w-3.5 h-3.5 text-greenx" /> : <Copy strokeWidth={1.5} className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}

      {qr && (
        <div className="grid grid-cols-3 gap-2.5 mt-8 w-full max-w-xs" data-testid="qr-share-actions">
          <button
            onClick={shareWhatsApp}
            data-testid="qr-whatsapp-btn"
            className="flex flex-col items-center gap-1.5 rounded-md py-3 text-xs text-cream bg-greenx active:scale-95 transition-transform"
          >
            <MessageCircle strokeWidth={1.25} className="w-4 h-4" /> WhatsApp
          </button>
          <button
            onClick={share}
            data-testid="qr-share-btn"
            className="flex flex-col items-center gap-1.5 bg-navy text-cream rounded-md py-3 text-xs active:scale-95 transition-transform"
          >
            <Share2 strokeWidth={1.25} className="w-4 h-4" /> Paylaş
          </button>
          <button
            onClick={download}
            data-testid="qr-download-btn"
            className="flex flex-col items-center gap-1.5 border border-line rounded-md py-3 text-xs text-ink active:scale-95 transition-transform"
          >
            <Download strokeWidth={1.25} className="w-4 h-4" /> İndir
          </button>
        </div>
      )}

      <p className="text-sm text-ink-soft text-center max-w-xs mt-8 leading-relaxed">
        Kasada okutun; indirim ve komisyon otomatik başlar.
      </p>
    </div>
  );
}
